package com.klantenstop.web.pages.about

import androidx.compose.runtime.Composable
import com.klantenstop.web.components.H1Heading
import com.klantenstop.web.components.H2Heading
import com.klantenstop.web.components.layout.PageLayout
import com.klantenstop.web.components.layout.PageSeoConfig
import com.klantenstop.web.styles.Typography
import com.materialkobweb.styles.MaterialColorVars
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.objectFit
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import org.jetbrains.compose.web.css.px

@Page("/over-ons")
@Composable
fun AboutPage() {
    val breakpoint = rememberBreakpoint()

    PageLayout(
        title = "Over KlantenStop: ons verhaal en missie",
        seo = PageSeoConfig.Managed(
            description = "Ontdek het verhaal achter KlantenStop: het Belgische platform dat wachtlijsten en klantenstops transparant maakt voor huisartsen, tandartsen en meer.",
            indexable = true,
            breadcrumbLabel = "Over ons",
        )
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(topBottom = if (breakpoint >= Breakpoint.MD) 64.px else 32.px),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(64.px)
        ) {
            // Image Section - Simple and Centered
            Image(
                modifier = Modifier
                    .fillMaxWidth()
                    .maxWidth(if (breakpoint >= Breakpoint.MD) 500.px else 350.px)
                    .objectFit(ObjectFit.Contain),
                src = "/images/Easier.png",
                alt = "KlantenStop - Easier healthcare search"
            )

            // Main Title & Origin Story
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .maxWidth(900.px)
                    .padding(leftRight = 24.px),
                verticalArrangement = Arrangement.spacedBy(24.px)
            ) {
                H1Heading(
                    modifier = Modifier.fillMaxWidth(),
                    text = "Wie zijn wij?",
                    color = MaterialColorVars.Primary.value()
                )

                SpanText(
                    modifier = Modifier
                        .fillMaxWidth()
                        .fontSize(if (breakpoint >= Breakpoint.MD) 18.px else 16.px)
                        .lineHeight(if (breakpoint >= Breakpoint.MD) 28.px else 24.px)
                        .color(MaterialColorVars.OnSurface.value()),
                    text = "KlantenStop ontstond uit persoonlijke ervaring. Na herhaaldelijk tegen een muur te lopen bij het zoeken naar een nieuwe huisarts of tandarts, realiseerden we ons dat dit probleem veel groter was dan onze eigen situatie."
                )
            }

            // All Sections in Cards Grid
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .maxWidth(1000.px)
                    .padding(leftRight = 24.px),
                verticalArrangement = Arrangement.spacedBy(32.px)
            ) {
                // Mission & How it Works Row
                SimpleGrid(
                    modifier = Modifier
                        .fillMaxWidth()
                        .gap(24.px),
                    numColumns = numColumns(1, 1, 2)
                ) {
                    // Mission Section Card
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(32.px)
                            .borderRadius(16.px)
                            .backgroundColor(MaterialColorVars.SurfaceContainer.value()),
                        verticalArrangement = Arrangement.spacedBy(16.px)
                    ) {
                        H2Heading(
                            typography = Typography.TitleLarge,
                            text = "Onze missie",
                            color = MaterialColorVars.Primary.value()
                        )
                        SpanText(
                            modifier = Modifier
                                .fontSize(if (breakpoint >= Breakpoint.MD) 16.px else 14.px)
                                .lineHeight(if (breakpoint >= Breakpoint.MD) 24.px else 20.px)
                                .color(MaterialColorVars.OnSurface.value()),
                            text = "We geloven dat informatie over beschikbaarheid van dienstverleners toegankelijk en betrouwbaar moet zijn. KlantenStop brengt deze verspreide informatie samen op één plek, zodat je niet langer websites moet afstruinen of tientallen bedrijven moet bellen."
                        )
                    }

                    // How it Works Section Card
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(32.px)
                            .borderRadius(16.px)
                            .backgroundColor(MaterialColorVars.SurfaceContainer.value()),
                        verticalArrangement = Arrangement.spacedBy(16.px)
                    ) {
                        H2Heading(
                            typography = Typography.TitleLarge,
                            text = "Hoe het werkt",
                            color = MaterialColorVars.Primary.value()
                        )
                        SpanText(
                            modifier = Modifier
                                .fontSize(if (breakpoint >= Breakpoint.MD) 16.px else 14.px)
                                .lineHeight(if (breakpoint >= Breakpoint.MD) 24.px else 20.px)
                                .color(MaterialColorVars.OnSurface.value()),
                            text = "Ons platform combineert het beste van twee werelden: community-gedreven updates zorgen voor actuele informatie, terwijl dienstverleners zelf hun gegevens kunnen beheren en verifiëren. Dit creëert een betrouwbaar ecosysteem waar iedereen bij wint."
                        )
                    }
                }

                // For Who & Engagement Row
                SimpleGrid(
                    modifier = Modifier
                        .fillMaxWidth()
                        .gap(24.px),
                    numColumns = numColumns(1, 1, 2)
                ) {
                    // For Who Section Card
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(32.px)
                            .borderRadius(16.px)
                            .backgroundColor(MaterialColorVars.SurfaceContainer.value()),
                        verticalArrangement = Arrangement.spacedBy(16.px)
                    ) {
                        H2Heading(
                            typography = Typography.TitleLarge,
                            text = "Voor wie?",
                            color = MaterialColorVars.Primary.value()
                        )
                        SpanText(
                            modifier = Modifier
                                .fontSize(if (breakpoint >= Breakpoint.MD) 16.px else 14.px)
                                .lineHeight(if (breakpoint >= Breakpoint.MD) 24.px else 20.px)
                                .color(MaterialColorVars.OnSurface.value()),
                            text = "Voor klanten die snel en efficiënt een dienstverlener willen vinden. Voor bedrijven die transparant willen communiceren over hun beschikbaarheid. Voor een betere dienstverlening."
                        )
                    }

                    // Engagement Section Card
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(32.px)
                            .borderRadius(16.px)
                            .backgroundColor(MaterialColorVars.SurfaceContainer.value()),
                        verticalArrangement = Arrangement.spacedBy(16.px)
                    ) {
                        H2Heading(
                            typography = Typography.TitleLarge,
                            text = "Ons engagement",
                            color = MaterialColorVars.Primary.value()
                        )
                        SpanText(
                            modifier = Modifier
                                .fontSize(if (breakpoint >= Breakpoint.MD) 16.px else 14.px)
                                .lineHeight(if (breakpoint >= Breakpoint.MD) 24.px else 20.px)
                                .color(MaterialColorVars.OnSurface.value()),
                            text = "We blijven het platform verbeteren op basis van feedback van onze community. Jouw ervaring staat centraal, omdat we uit eigen ervaring weten hoe frustrerend deze zoektocht kan zijn."
                        )
                    }
                }
            }
        }
    }
}
